<script setup lang="ts">
import { api } from '@/api';
import { DataSetResponseDto } from '@/api/generator';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { reloadTrendsKey } from '@/components/TrendChart/types';
import { routenames } from '@/router/routenames.ts';
import { truthy } from '@/utils/helper';
import { provide, ref } from 'vue';
import { LocationQueryValue, useRoute, useRouter } from 'vue-router';
import TrendChartOverview from './components/TrendChartOverview.vue';
import { useLoading } from '@/utils/useLoading';
import { entityNotFoundErrorHandler } from '@/errorHandling';

const showAllCells = ref(false);

const route = useRoute();
const router = useRouter();

function ensureLocationQueryArray(locationQuery: LocationQueryValue | LocationQueryValue[]) {
  return [locationQuery].flat().filter(truthy);
}

const startDate = String(route.query.start);
const endDate = String(route.query.end);
const dataSetIds = ensureLocationQueryArray(route.query.dataSets);

const dataSetResponse = ref<DataSetResponseDto>();

const { action: loadDataSets, loadingFirstTime } = useLoading(async () => {
  const { result } = await api.dataSetApi.getDataPoints(
    {
      dataPointRequestDto: {
        startDate: startDate,
        endDate: endDate,
        dataSetIds,
      },
    },
    [entityNotFoundErrorHandler(router)],
  );
  if (result) {
    dataSetResponse.value = result;
  }
}, true);

provide(reloadTrendsKey, loadDataSets);

loadDataSets();
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Trends')" />
    </template>
    <template #info>
      <VSwitch v-model="showAllCells" color="primary" :label="$t('ShowAllCells')" hideDetails />
    </template>
    <template #actions>
      <span class="text-subtitle-1 font-weight-semibold">{{ $t('TimeRange') }}:</span
      >&nbsp;<DateTimeFormatter :value="startDate" />&nbsp;-&nbsp;<DateTimeFormatter
        :value="endDate"
      />
      <PrimaryVBtn
        class="ml-4"
        :to="{ name: routenames.trendSelection }"
        :text="$t('BackToOverview')"
      />
    </template>
    <div v-if="dataSetResponse?.dataSets" class="chart">
      <TrendChartOverview
        class="mt-4"
        :totalDataSetCount="dataSetResponse.totalDataSetCount"
        :dataSets="dataSetResponse.dataSets"
        :startDate="startDate"
        :endDate="endDate"
        :showDifferences="!showAllCells"
      />
    </div>
  </BasePage>
</template>
